import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import {
  EtbPeriod,
  EtbPeriods,
  EtbPreview,
  EtbSplitGroup,
  EtbSummary,
  fetchEtbPeriods,
  fetchEtbSplitGroups,
  generateEtb,
  previewEtbPdf,
} from '../../api/batch6Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the six source files (and optionally an extra workbook whose sheets are copied in unchanged).',
  "Confirm which of the Digita PDF's two period columns is the prior year, and preview the extraction.",
  'Split the prior-year figure of any Digita code that maps to several QB accounts, then click Generate ETB Workbook.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';
const money = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : null);

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6 rounded-lg border border-border p-4">
      <h3 className="mb-2 text-sm font-bold text-foreground">
        {n}. {title}
      </h3>
      {children}
    </div>
  );
}

function EtbInterlinkPanel({ mission, onBack }: PanelProps) {
  const [coa, setCoa] = useState<File[]>([]);
  const [mapping, setMapping] = useState<File[]>([]);
  const [qbCurrent, setQbCurrent] = useState<File[]>([]);
  const [qbLast, setQbLast] = useState<File[]>([]);
  const [clientLast, setClientLast] = useState<File[]>([]);
  const [apPdf, setApPdf] = useState<File[]>([]);
  const [extra, setExtra] = useState<File[]>([]);

  const [periods, setPeriods] = useState<EtbPeriods | null>(null);
  const [usePeriod, setUsePeriod] = useState<EtbPeriod>('period2');
  const [preview, setPreview] = useState<EtbPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [groups, setGroups] = useState<EtbSplitGroup[] | null>(null);
  const [splitValues, setSplitValues] = useState<Record<string, string>>({});
  const [stepError, setStepError] = useState<string | null>(null);

  const { status, error, result, run } = useFileRun<FileResult<EtbSummary>>();

  const pdf = apPdf[0] ?? null;
  const allUploaded = Boolean(coa[0] && mapping[0] && qbCurrent[0] && qbLast[0] && clientLast[0] && pdf);

  // Read the PDF's period dates whenever a new PDF is chosen.
  useEffect(() => {
    setPeriods(null);
    setPreview(null);
    if (!pdf) return;
    let cancelled = false;
    fetchEtbPeriods(pdf)
      .then((p) => !cancelled && setPeriods(p))
      .catch(() => !cancelled && setPeriods({ period1: null, period2: null }));
    return () => {
      cancelled = true;
    };
  }, [pdf]);

  // Look for Digita codes shared by several QB accounts.
  useEffect(() => {
    setGroups(null);
    setStepError(null);
    if (!(coa[0] && mapping[0] && pdf)) return;
    let cancelled = false;
    fetchEtbSplitGroups(coa[0], mapping[0], pdf, usePeriod)
      .then((g) => {
        if (cancelled) return;
        setGroups(g);
        const defaults: Record<string, string> = {};
        g.forEach((grp) =>
          grp.qb_names.forEach((qb, idx) => {
            defaults[`${grp.code}|${qb}`] = String(idx === 0 ? grp.pdf_value ?? 0 : 0);
          })
        );
        setSplitValues(defaults);
      })
      .catch((err) => !cancelled && setStepError(err instanceof Error ? err.message : 'Could not check shared codes.'));
    return () => {
      cancelled = true;
    };
  }, [coa, mapping, pdf, usePeriod]);

  const handlePreview = async () => {
    if (!(coa[0] && pdf)) return;
    setPreviewing(true);
    setStepError(null);
    try {
      setPreview(await previewEtbPdf(coa[0], pdf));
    } catch (err) {
      setPreview(null);
      setStepError(err instanceof Error ? err.message : 'PDF extraction failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (!allUploaded) return;
    const overrides = (groups ?? []).flatMap((g) =>
      g.qb_names.map((qb) => ({ code: g.code, qb_name: qb, value: Number(splitValues[`${g.code}|${qb}`]) || 0 }))
    );
    await run(
      () =>
        generateEtb(
          {
            coa: coa[0],
            mapping: mapping[0],
            qbCurrent: qbCurrent[0],
            qbLast: qbLast[0],
            clientLast: clientLast[0],
            apPdf: pdf,
            extra: extra[0] ?? null,
          },
          usePeriod,
          overrides,
        ),
      'Building the workbook failed.',
    );
  };

  const drop = (label: string, files: File[], set: (f: File[]) => void, accept: string, hint: string, required = true) => (
    <FileDrop label={label} required={required} accept={accept} hint={hint} accentColor={mission.color} files={files} onFiles={set} />
  );

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <Step n={1} title="Upload source files">
        <div className="grid gap-x-4 sm:grid-cols-2">
          {drop('Chart of Accounts (.xls)', coa, setCoa, '.xls', 'XLS')}
          {drop('QB Last Year TB (.xlsx)', qbLast, setQbLast, '.xlsx', 'XLSX')}
          {drop('Ap to QB mapping (.xlsx)', mapping, setMapping, '.xlsx', 'XLSX')}
          {drop('Last Year Client TB (.xlsx)', clientLast, setClientLast, '.xlsx', 'XLSX')}
          {drop('QB Current Year TB (.xlsx)', qbCurrent, setQbCurrent, '.xlsx', 'XLSX')}
          {drop('Digita AP Trial Balance (.pdf)', apPdf, setApPdf, '.pdf', 'PDF')}
        </div>
        <FileDrop
          label="Optional: extra workbook to copy sheets from as-is (.xlsx)"
          note="Every sheet is copied unchanged (values, formulas, fonts, fills, borders, number formats, merged cells, sizes). Images, charts, conditional formatting and data validation are not carried over. A name clash gets a ' (2)' suffix."
          accept=".xlsx"
          hint="XLSX"
          accentColor={mission.color}
          files={extra}
          onFiles={setExtra}
        />
      </Step>

      <Step n={2} title="Confirm the prior-year column">
        <p className="mb-2 text-xs text-muted-foreground">
          The PDF has two period columns, and which one is the prior year depends on when the client pulled the report, not
          on column position. Check the dates against the year you're building this ETB for.
        </p>
        {pdf ? (
          <>
            <select className={selectClass} value={usePeriod} onChange={(e) => setUsePeriod(e.target.value as EtbPeriod)} aria-label="Prior-year period">
              {(['period2', 'period1'] as EtbPeriod[]).map((key) => (
                <option key={key} value={key}>
                  {key === 'period1' ? 'Period 1' : 'Period 2'} — {fmtDate(periods?.[key] ?? null) ?? 'date not detected'}
                </option>
              ))}
            </select>
            {periods && (!periods.period1 || !periods.period2) && (
              <p className="mt-2 text-xs text-amber-700">
                Could not read one or both "Period ending" dates from the PDF. Double-check the choice manually.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Upload the Digita AP Trial Balance PDF above to confirm which period is the prior year.</p>
        )}
      </Step>

      <Step n={3} title="Preview PDF extraction (optional but recommended)">
        <Button variant="outline" onClick={handlePreview} disabled={!(coa[0] && pdf) || previewing}>
          {previewing ? 'Extracting…' : 'Preview PDF extraction'}
        </Button>
        {preview && (
          <div className="mt-3 space-y-2">
            <div className="max-h-72 overflow-auto rounded-md border border-border">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-muted text-muted-foreground">
                  <tr>
                    {preview.columns.map((c) => (
                      <th key={c} className="px-3 py-1.5 font-semibold">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-1.5">
                          {typeof cell === 'number' ? money(cell) : cell ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Period 1: debit {money(preview.totals.p1_debit)} / credit {money(preview.totals.p1_credit)} · Period 2: debit{' '}
              {money(preview.totals.p2_debit)} / credit {money(preview.totals.p2_credit)}
              {usePeriod === 'period1' ? ' (Period 1 is the prior year)' : ' (Period 2 is the prior year)'}
            </p>
            {preview.balanced ? (
              <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">Both periods balance (debit = credit). Extraction looks clean.</p>
            ) : (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Debit and credit totals don't balance for one of the periods. Double-check the extraction against the PDF.
              </p>
            )}
          </div>
        )}
      </Step>

      <Step n={4} title="Split shared prior-year figures (if any)">
        <p className="mb-2 text-xs text-muted-foreground">
          A Digita code mapped to more than one QB account has only one prior-year figure in the PDF. It defaults to the full
          figure on the first account and 0 on the rest: review and redistribute it.
        </p>
        {stepError && <ErrorNotice message={stepError} />}
        {!(coa[0] && mapping[0] && pdf) && (
          <p className="text-sm text-muted-foreground">Upload the Chart of Accounts, Mapping and PDF files to check for shared codes.</p>
        )}
        {groups && groups.length === 0 && (
          <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">No Digita code maps to more than one QB account: nothing to split.</p>
        )}
        {groups?.map((g) => {
          const entered = g.qb_names.reduce((sum, qb) => sum + (Number(splitValues[`${g.code}|${qb}`]) || 0), 0);
          const matches = g.pdf_value === null || Math.abs(entered - g.pdf_value) <= 0.01;
          return (
            <div key={g.code} className="mb-4">
              <p className="text-sm font-semibold text-foreground">
                {g.code}: {g.description}
              </p>
              <p className="mb-2 text-xs text-muted-foreground">
                {g.pdf_value === null
                  ? 'No prior-year figure found in the PDF for this code: leave at 0 or fill in manually.'
                  : `PDF reports ${money(g.pdf_value)} combined for these ${g.qb_names.length} QB accounts.`}
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {g.qb_names.map((qb) => (
                  <div key={qb}>
                    <label className="mb-1 block text-xs text-muted-foreground" htmlFor={`${g.code}-${qb}`}>
                      {qb}
                    </label>
                    <Input
                      id={`${g.code}-${qb}`}
                      type="number"
                      step="0.01"
                      value={splitValues[`${g.code}|${qb}`] ?? '0'}
                      onChange={(e) => setSplitValues((v) => ({ ...v, [`${g.code}|${qb}`]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
              {g.pdf_value !== null && (
                <p className={`mt-1 text-xs ${matches ? 'text-green-700' : 'text-amber-700'}`}>
                  {matches ? 'Entered total matches the PDF figure.' : `Entered total ${money(entered)} does not match the PDF's ${money(g.pdf_value)}.`}
                </p>
              )}
            </div>
          );
        })}
      </Step>

      <Step n={5} title="Generate workbook">
        <Button
          onClick={handleGenerate}
          disabled={!allUploaded || status === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="w-full text-white hover:opacity-90"
        >
          {status === 'loading' ? 'Building…' : 'Generate ETB Workbook'}
        </Button>
        {status === 'loading' && <ProcessingNotice label={'Building…'} />}
        {!allUploaded && <InfoNotice>Upload all six files above to enable generation.</InfoNotice>}
        {status === 'error' && error && <ErrorNotice message={error} />}
        {status === 'success' && result && s && (
          <div className="mt-4 space-y-3 text-sm">
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ['A3-ETB rows', s.etb_rows],
                ['AP-BS rows', s.bs_rows],
                ['AP-P&L rows', s.pl_rows],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                  <div className="text-lg font-bold text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            {s.dupe_codes.length > 0 && (
              <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
                {s.dupe_codes.length} Digita code(s) map to more than one QB account: {s.dupe_codes.join(', ')}. These rows are
                highlighted amber; totals involving them may be double-counted and should be reviewed manually.
              </div>
            )}
            {s.copied_sheet_names.length > 0 && (
              <div className="rounded-lg bg-blue-50 px-4 py-3 text-blue-700">Copied in as-is: {s.copied_sheet_names.join(', ')}</div>
            )}
          </div>
        )}
      </Step>
    </MissionShell>
  );
}

export default EtbInterlinkPanel;
